import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { afterTransitions, fadeAfter } from "../motion.js";
import { Icon } from "./icons.js";
import { uploadFile } from "../index.js";
/** Thumbnails for uploaded files: photos inline, everything else as a labelled tile. Tap to open. */
export function Thumbs({ files, onRemove }) {
    if (!files.length)
        return null;
    return (_jsx("div", { className: "tc-thumbs", children: files.map((f) => (_jsxs("div", { className: "tc-thumb", children: [_jsx("a", { href: f.url, target: "_blank", rel: "noreferrer", title: f.name, children: f.mime.startsWith("image/") && f.mime !== "image/heic" ? _jsx("img", { src: f.url, alt: f.name, loading: "lazy" })
                        : _jsxs("span", { className: "tc-thumb-file", children: [f.mime.startsWith("video/") ? "▶ Video" : f.mime === "application/pdf" ? "PDF" : "File", _jsx("small", { children: f.name })] }) }), onRemove && _jsx("button", { type: "button", className: "tc-thumb-x", "aria-label": `Remove ${f.name}`, onClick: () => onRemove(f.id), children: "\u00D7" })] }, f.id))) }));
}
/**
 * Add photos or files. On a phone "Take photo" opens the camera directly.
 * Files upload straight away; the parent gets their ids to attach when the form is saved.
 */
export function FilePicker({ module, site, files, onChange, photosOnly, label = "Add photos" }) {
    const camera = useRef(null);
    const picker = useRef(null);
    const [busy, setBusy] = useState(0);
    const [error, setError] = useState(null);
    async function add(list) {
        if (!list?.length)
            return;
        setError(null);
        const out = [...files];
        for (const f of Array.from(list)) {
            setBusy((n) => n + 1);
            try {
                out.push(await uploadFile(module, site, f));
                onChange([...out]);
            }
            catch (err) {
                setError(`${f.name}: ${err.message}`);
            }
            finally {
                setBusy((n) => n - 1);
            }
        }
    }
    return (_jsxs("div", { className: "tc-picker", children: [_jsx(Thumbs, { files: files, onRemove: (id) => onChange(files.filter((f) => f.id !== id)) }), _jsxs("div", { className: "tc-picker-buttons", children: [_jsx("button", { type: "button", className: "tc-btn", onClick: () => camera.current?.click(), children: "\uD83D\uDCF7 Take photo" }), _jsx("button", { type: "button", className: "tc-btn", onClick: () => picker.current?.click(), children: label }), busy > 0 && _jsx("span", { className: "muted", children: "Uploading\u2026" })] }), _jsx("input", { ref: camera, type: "file", accept: "image/*", capture: "environment", hidden: true, onChange: (e) => { void add(e.target.files); e.target.value = ""; } }), _jsx("input", { ref: picker, type: "file", multiple: true, hidden: true, accept: photosOnly ? "image/*" : "image/*,video/*,application/pdf,.docx,.xlsx,.pptx,.csv,.txt", onChange: (e) => { void add(e.target.files); e.target.value = ""; } }), error && _jsx("div", { className: "tc-error", children: error })] }));
}
/**
 * Segmented tabs (status filters etc.), with optional counts. A thumb slides to the chosen tab (150ms) and the content
 * below fades in; the tab strip, its labels and everything around the page stay still.
 */
export function Tabs({ value, onChange, items }) {
    const ref = useRef(null);
    const [thumb, setThumb] = useState(null);
    useLayoutEffect(() => {
        const el = ref.current?.querySelector('[aria-selected="true"]');
        if (!el) {
            setThumb(null);
            return;
        }
        const place = () => setThumb({ x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight });
        place();
        const ro = new ResizeObserver(place);
        ro.observe(el);
        if (ref.current)
            ro.observe(ref.current);
        return () => ro.disconnect();
    }, [value, items.length]);
    // Fade the right edge only while more tabs are hidden there, and bring the chosen tab into view.
    useLayoutEffect(() => {
        const box = ref.current;
        if (!box)
            return;
        const check = () => { if (box.scrollWidth - box.scrollLeft - box.clientWidth > 2)
            box.dataset.over = "";
        else
            delete box.dataset.over; };
        check();
        const on = box.querySelector('[aria-selected="true"]');
        if (on && (on.offsetLeft + on.offsetWidth > box.scrollLeft + box.clientWidth - 30 || on.offsetLeft < box.scrollLeft))
            box.scrollTo({ left: on.offsetLeft - 24, behavior: "smooth" });
        const ro = new ResizeObserver(check);
        ro.observe(box);
        box.addEventListener("scroll", check, { passive: true });
        return () => { ro.disconnect(); box.removeEventListener("scroll", check); };
    }, [value, items.length]);
    const pick = (v) => {
        if (v === value)
            return;
        flushSync(() => onChange(v));
        fadeAfter(ref.current);
    };
    return (_jsxs("div", { className: `tc-tabs${thumb ? " has-thumb" : ""}`, role: "tablist", ref: ref, children: [thumb && _jsx("span", { className: "tc-tabs-thumb", "aria-hidden": "true", style: { width: thumb.w, height: thumb.h, top: 0, transform: `translate(${thumb.x}px, ${thumb.y}px)` } }), items.map((i) => (_jsxs("button", { role: "tab", "aria-selected": value === i.value, className: "tc-tab-btn", onClick: () => pick(i.value), children: [i.label, i.count !== undefined && _jsx("span", { className: "tc-tab-count", children: i.count })] }, i.value)))] }));
}
export function EmptyState({ title, children, icon = "check-circle" }) {
    return _jsxs("div", { className: "tc-empty", children: [_jsx("span", { className: "tc-empty-art", children: _jsx(Icon, { name: icon, size: 26 }) }), _jsx("b", { children: title }), children && _jsx("span", { className: "muted", children: children })] });
}
/** A shimmering block that holds the space of what's loading. */
export function Skeleton({ w, h = 14, r, style }) {
    return _jsx("span", { className: "tc-skel", "aria-hidden": "true", style: { width: w ?? "100%", height: h, borderRadius: r, ...style } });
}
/** In place of "Loading…": a few shimmering lines, roughly the height of what's coming. */
export function Loading({ lines = 4, card }) {
    const body = _jsx("div", { className: "tc-skel-lines", role: "status", "aria-label": "Loading", children: Array.from({ length: lines }, (_, i) => _jsx(Skeleton, {}, i)) });
    return card ? _jsx("div", { className: "tc-card tc-skel-card", children: body }) : body;
}
/** The whole page while it loads: the banner in place and cards shaped like the real ones, so nothing jumps. */
export function PageSkeleton({ cards = 3 }) {
    return (_jsxs("div", { className: "tc-skel-page", role: "status", "aria-label": "Loading", children: [_jsx("header", { className: "tc-banner", children: _jsx("div", { className: "tc-banner-row", children: _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 10 }, children: [_jsx(Skeleton, { w: 160, h: 14 }), _jsx(Skeleton, { w: 280, h: 30, r: 10 })] }) }) }), _jsx("div", { className: "tc-content", children: Array.from({ length: cards }, (_, i) => (_jsxs("div", { className: "tc-card tc-skel-card", children: [_jsx(Skeleton, { w: i ? "36%" : "28%", h: 18 }), _jsx("div", { className: "tc-skel-lines", children: Array.from({ length: 3 + (i % 2) }, (_, k) => _jsx(Skeleton, {}, k)) })] }, i))) })] }));
}
/**
 * A number whose digits roll into place like a departure board: from zero the first time it's shown (once the page
 * has faded in), and from the old value to the new one when it changes.
 */
export function CountUp({ value, format }) {
    return _jsx(Roll, { text: format(value) });
}
export function Roll({ text }) {
    const [shown, setShown] = useState(() => text.replace(/\d/g, "0"));
    useEffect(() => {
        let alive = true;
        if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            setShown(text);
            return;
        }
        void afterTransitions().then(() => requestAnimationFrame(() => requestAnimationFrame(() => { if (alive)
            setShown(text); })));
        return () => { alive = false; };
    }, [text]);
    // Line the old and new strings up from the right, so units stay put and digits roll in their own column.
    const chars = [...text];
    const old = [...shown].reverse();
    return (_jsx("span", { className: "tc-roll", "aria-label": text, children: chars.map((c, i) => {
            const fromRight = chars.length - 1 - i;
            if (!/\d/.test(c))
                return _jsx("span", { className: "tc-roll-c", "aria-hidden": "true", children: c }, `c${fromRight}`);
            const d = Number(/\d/.test(old[fromRight] ?? "") ? old[fromRight] : 0);
            return (_jsx("span", { className: "tc-roll-d", "aria-hidden": "true", children: _jsx("span", { style: { transform: `translateY(${-d * 10}%)`, transitionDelay: `${Math.min(fromRight, 8) * 28}ms` }, children: DIGITS }) }, `d${fromRight}`));
        }) }));
}
const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => _jsx("i", { children: n }, n));
